import {
	ActionBar,
	Input,
	SpinnerWithBackground,
	TextArea,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { useEnpsStore } from '../store'
import type { EnpsGeneralTabInput } from './types'

/**
 * @fileoverview eNPS General Tab
 *
 * Basic information for eNPS survey configuration (name, description)
 */

export const EnpsGeneralTab = () => {
	const { organizationId: _organizationId } = useParams<{
		organizationId: string
		enpsId: string
	}>()
	const navigate = useNavigate()
	const toast = useToast()
	const { enpsConfig, useCreateEnpsConfig, useEditEnpsConfig } = useEnpsStore()
	const { createEnpsConfig, isLoading: isCreating } = useCreateEnpsConfig()
	const { editEnpsConfig, isLoading: isEditing } = useEditEnpsConfig(enpsConfig?.id)

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty },
		reset,
	} = useForm<EnpsGeneralTabInput>({
		defaultValues: {
			name: enpsConfig?.name || '',
			description: enpsConfig?.description || '',
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	useEffect(() => {
		if (enpsConfig) {
			reset({
				name: enpsConfig.name || '',
				description: enpsConfig.description || '',
			})
		}
	}, [enpsConfig, reset])

	const handleSubmitInternal = async (data: EnpsGeneralTabInput) => {
		if (!enpsConfig) return

		try {
			if (enpsConfig.id) {
				await editEnpsConfig({
					name: data.name,
					description: data.description || null,
				})
			} else {
				await createEnpsConfig({
					name: data.name,
					description: data.description || null,
					cadence: 'quarterly',
					populationId: null,
					emailTemplateId: null,
					questionText: 'How likely are you to recommend our organization as a place to work?',
					followUpQuestionText: 'What is the primary reason for your score?',
					textFeedbackEnabled: true,
					nextScheduledDate: null,
					allowExternalParticipants: false,
				})
			}
			reset(data)
		} catch (err) {
			// Mutation errors are handled by the mutation's onError callback.
			// Surface non-mutation errors (e.g., missing store data) to the user.
			if (err instanceof Error && !(err as any).data?.code) {
				toast.showToast(err.message, 'error')
			}
		}
	}

	const isPending = isCreating || isEditing

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 xl:gap-x-12 gap-6 px-6 items-start'>
				<div>
					<Controller
						name='name'
						control={control}
						rules={{
							required: 'Name is required',
							minLength: {
								value: 3,
								message: 'Name must be at least 3 characters',
							},
						}}
						render={({ field }) => (
							<Input
								label='Survey Name'
								placeholder='e.g., Quarterly NPS Survey'
								variant={errors.name ? 'error' : 'default'}
								hint={errors.name?.message as string}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								required
							/>
						)}
					/>
				</div>
				<div>
					<Controller
						name='description'
						control={control}
						render={({ field }) => (
							<TextArea
								label='Description (Optional)'
								placeholder='Describe the purpose of this NPS survey'
								value={field.value || ''}
								onChange={field.onChange}
								onBlur={field.onBlur}
								rows={4}
							/>
						)}
					/>
				</div>
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Cancel',
						onClick: () => navigate('/enps'),
					},
					{
						variant: 'primary' as const,
						label: enpsConfig?.id ? 'Save' : 'Create Survey',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
